import { useEffect, useRef, useSyncExternalStore, type MouseEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { CLIENT_ID } from './api';
import { useLiveSocket } from './ws';

/**
 * Live updates — every open dashboard stays current without F5:
 *   - `changes` events (web-api plugins/liveChanges.ts: someone saved
 *     something) and successful bot `action-result`s are mapped to the
 *     affected queries, and only those reload
 *   - while the page has unsaved input ("dirty": typed / picked something
 *     since the last save), form data does NOT reload — LiveBanners offers
 *     "Wurde gerade von X geändert — Neu laden" instead
 *   - back in the tab after a while, or after a reconnect, what's on screen
 *     reloads (same rule for forms)
 *   - a new dashboard build (index.html points to another bundle) shows
 *     "Neue Version verfügbar — Neu laden"; never reloads by itself
 */

/** Query roots that feed forms — held back while the page has unsaved input. */
const FORM_ROOTS = ['settings', 'panel', 'games-catalog', 'warn-roles', 'command-perms', 'self-roles', 'admin-bot-profile'];

/** Per-server query roots (`[root, guildId, …]`) — "everything of this server". */
const GUILD_ROOTS = [
  'settings', 'guildSettings', 'members', 'warnings', 'self-roles', 'permabans', 'media', 'guild-roles',
  'guild-channels', 'games-catalog', 'warn-roles', 'panel', 'leaderboard', 'command-perms', 'backups',
  'posted', 'giveaways', 'tickets', 'access-guild', 'protection-status', 'protection-incidents',
];

/** Not reloaded on tab return / reconnect (external or never changing). */
const NO_REFRESH = ['lyrics', 'emojis-all', 'user'];

const forGuild = (roots: string[], guildId: string): QueryKey[] => roots.map((root) => [root, guildId]);

/** Which data a dashboard change touched — by the path of the web-api route. */
export function keysForChange(path: string, guildId: string | null): QueryKey[] {
  const p = path.replace(/^\/api/, '');
  const guild = p.match(/^\/guilds\/(\d+)\/(.+)$/);
  if (guild) {
    const [, id, rest] = guild;
    if (rest === 'settings') return [...forGuild(['settings', 'guildSettings'], id), ['guilds']];
    if (rest.startsWith('panels/')) return forGuild(['panel', 'settings', 'guildSettings'], id);
    if (rest === 'games') return forGuild(['games-catalog', 'settings'], id);
    if (rest.startsWith('command-perms')) return forGuild(['command-perms'], id);
    if (rest.startsWith('leaderboard')) return forGuild(['leaderboard', 'members'], id);
    if (rest.startsWith('media')) return forGuild(['media'], id);
    if (rest.startsWith('self-roles')) return forGuild(['self-roles', 'posted', 'settings'], id);
    if (rest.startsWith('warn-roles')) return forGuild(['warn-roles'], id);
    if (rest.startsWith('warnings')) return forGuild(['warnings', 'members', 'leaderboard'], id);
    if (rest.startsWith('posted')) return forGuild(['posted', 'self-roles'], id);
    if (rest.startsWith('protection/')) return forGuild(['protection-status', 'protection-incidents'], id);
    if (rest.startsWith('playlists')) return forGuild(['playlists', 'playlist'], id);
    if (rest.startsWith('music/radios')) return forGuild(['radios'], id);
    if (rest.startsWith('music/history')) return forGuild(['music-history'], id);
    return forGuild(GUILD_ROOTS, id);
  }
  // Rights may have changed → also the own rights (me) and the server list.
  if (p.startsWith('/access/')) return [['access-overview'], ['access-guild'], ['access-activity'], ['me'], ['guilds']];
  const plugins = p.match(/^\/admin\/guilds\/(\d+)\/plugins$/);
  if (plugins) return [['admin-overview'], ['guilds'], ...forGuild(['guildSettings', 'settings'], plugins[1])];
  if (p === '/admin/bot-profile') return [['admin-bot-profile'], ['bot-profile']];
  if (p.startsWith('/admin/')) return [['admin-overview']];
  if (p.startsWith('/backups/')) return [['backups']];
  return guildId ? forGuild(GUILD_ROOTS, guildId) : [];
}

/** Bot actions (CommandDispatcher) → the data their result changes. */
const ACTION_ROOTS: Array<[RegExp, string[]]> = [
  [/^(ban|kick|timeout|member-setnick|assign-role)$/, ['members']],
  [/^(permaban|permaunban)$/, ['permabans', 'members']],
  [/^warn-/, ['warnings', 'members', 'leaderboard']],
  [/^giveaway-/, ['giveaways']],
  [/^ticket-reply$/, ['tickets']],
  [/^(send-message|send-embed|rules-post|support-rules-post|tickets-post-panel|nsfw-post|posted-update)$/, ['posted']],
  [/^games-setup$/, ['posted', 'games-catalog', 'guild-channels']],
  [/^setup-server-stats$/, ['settings', 'guildSettings', 'guild-channels']],
  [/^channel-/, ['guild-channels']],
  [/^create-backup$/, ['backups']],
  // The bot noted the end in the incident after the web-api lifted the pause.
  [/^raid-lockdown-end$/, ['protection-status', 'protection-incidents']],
  // Rebuilt server: roles, channels, settings … all new.
  [/^(restore-backup|guild-clone)$/, GUILD_ROOTS],
];

export function keysForAction(action: string, guildId: string | null): QueryKey[] {
  const roots = ACTION_ROOTS.find(([re]) => re.test(action))?.[1] ?? [];
  const keys = guildId ? forGuild(roots, guildId) : [];
  if (action === 'ticket-reply') keys.push(['ticket']);
  return keys;
}

// ---------------------------------------------------------------- state

interface LiveState {
  /** The open page has input that isn't saved yet. */
  dirty: boolean;
  /** Changes held back because of `dirty`: queries to reload + who changed them. */
  held: { keys: QueryKey[]; by: string[] } | null;
  newVersion: boolean;
}

let state: LiveState = { dirty: false, held: null, newVersion: false };
const listeners = new Set<() => void>();
const update = (patch: Partial<LiveState>) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};

export function useLiveState(): LiveState {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => state
  );
}

export function markDirty() {
  if (!state.dirty) update({ dirty: true });
}

/**
 * Props for the content area: typing or picking anything there counts as
 * unsaved input (navigation links and the sidebar don't). React events also
 * bubble out of portals, so popover pickers are covered.
 */
export const dirtyTracking = {
  onInputCapture: markDirty,
  onChangeCapture: markDirty,
  onClickCapture: (e: MouseEvent) => {
    const target = e.target as Element | null;
    if (!target?.closest || target.closest('a[href], aside, nav, [data-live-ignore]')) return;
    if (target.closest('button, input, select, textarea, label, [role="option"], [role="menuitem"], [role="button"]')) {
      markDirty();
    }
  },
};

const sameKey = (a: QueryKey, b: QueryKey) => JSON.stringify(a) === JSON.stringify(b);
const isFormKey = (key: QueryKey) => FORM_ROOTS.includes(String(key[0]));
const isOnScreen = (qc: QueryClient, key: QueryKey) => qc.getQueryCache().findAll({ queryKey: key, type: 'active' }).length > 0;

/** Reloads `keys`; with `hold`, form data on screen waits while the page is dirty. */
function applyChange(qc: QueryClient, keys: QueryKey[], by: string, hold: boolean) {
  const held: QueryKey[] = [];
  for (const key of keys) {
    if (hold && state.dirty && isFormKey(key) && isOnScreen(qc, key)) held.push(key);
    else void qc.invalidateQueries({ queryKey: key });
  }
  if (!held.length) return;
  const prev = state.held ?? { keys: [], by: [] };
  update({
    held: {
      keys: [...prev.keys, ...held.filter((k) => !prev.keys.some((p) => sameKey(p, k)))],
      by: prev.by.includes(by) ? prev.by : [...prev.by, by],
    },
  });
}

/** "Neu laden": drop the unsaved input, load what others changed. */
export function reloadHeld(qc: QueryClient) {
  const keys = state.held?.keys ?? [];
  update({ held: null, dirty: false });
  keys.forEach((key) => void qc.invalidateQueries({ queryKey: key }));
}

/** "×": keep editing (saving then overwrites the other change). */
export function dismissHeld() {
  update({ held: null });
}

/** Everything on screen, except held-back form data and NO_REFRESH. */
function refreshOnScreen(qc: QueryClient) {
  void qc.invalidateQueries({
    predicate: (q) => !NO_REFRESH.includes(String(q.queryKey[0])) && !(state.dirty && isFormKey(q.queryKey)),
  });
}

// ---------------------------------------------------------------- new version

const bundleOf = (html: string | null | undefined) => html?.match(/\/assets\/index-[\w-]+\.js/)?.[0] ?? null;
// Dev server (no hashed bundle) → no version check.
const RUNNING_BUNDLE = bundleOf(
  document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')?.getAttribute('src')
);
let dismissedBundle: string | null = null;

async function checkVersion() {
  if (!RUNNING_BUNDLE || state.newVersion) return;
  try {
    const res = await fetch(`/?v=${Date.now()}`, { cache: 'no-store' });
    const live = bundleOf(await res.text());
    if (live && live !== RUNNING_BUNDLE && live !== dismissedBundle) {
      dismissedBundle = live;
      update({ newVersion: true });
    }
  } catch {
    // offline / deploy in progress — next check
  }
}

export function dismissNewVersion() {
  update({ newVersion: false });
}

// ---------------------------------------------------------------- sync

/** Mounted once in the App shell. */
export function useLiveSync() {
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const connectedBefore = useRef(false);

  // Leaving a page drops its unsaved input — and with it any hold.
  useEffect(() => {
    update({ dirty: false, held: null });
  }, [pathname]);

  // A successful save (any mutation): the form matches the server again —
  // now load what was held back.
  useEffect(
    () =>
      qc.getMutationCache().subscribe((event) => {
        if (event.type !== 'updated' || event.action.type !== 'success') return;
        if (state.held) reloadHeld(qc);
        else if (state.dirty) update({ dirty: false });
      }),
    [qc]
  );

  useLiveSocket(
    (msg) => {
      if (msg.channel === 'changes') {
        const change = msg.payload as { path: string; guildId: string | null; actorName: string; clientId: string | null };
        // Own tab: it reloads after saving anyway — never hold its own change.
        applyChange(qc, keysForChange(change.path, change.guildId), `von ${change.actorName}`, change.clientId !== CLIENT_ID);
      } else if (msg.channel === 'bot:event') {
        const ev = msg.payload as { type?: string; action?: string; guildId?: string | null; success?: boolean };
        if (ev.type === 'action-result' && ev.success && ev.action) {
          applyChange(qc, keysForAction(ev.action, ev.guildId ?? null), 'durch eine Bot-Aktion', true);
        }
        // Server-Schutz stepped in: new incident (bot plugins/Admin/lib/protection.js), maybe a lockdown.
        if (ev.type === 'protection-incident' && ev.guildId) {
          void qc.invalidateQueries({ queryKey: ['protection-incidents', ev.guildId] });
          void qc.invalidateQueries({ queryKey: ['protection-status', ev.guildId] });
        }
      }
    },
    () => {
      // Reconnect (not the first connect): events may have been missed.
      if (connectedBefore.current) {
        refreshOnScreen(qc);
        void checkVersion();
      }
      connectedBefore.current = true;
    }
  );

  // Back in the tab after a while: reload what's on screen; check for a new build.
  useEffect(() => {
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt > 5_000) {
        refreshOnScreen(qc);
        void checkVersion();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    const timer = window.setInterval(() => void checkVersion(), 10 * 60_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(timer);
    };
  }, [qc]);
}
